import { normalizeRounds } from '@nexbit/types'
import type { DrawRoundConfig, DrawScheduleConfig } from '@nexbit/types'

/**
 * The rounds half of a lottery's timetable, as the settings form works with it.
 *
 * A lottery may announce more than once a day (หวยหุ้นจีน: รอบเช้า 10:30, รอบบ่าย 14:00).
 * The form holds a draft per round; these turn drafts into the config the API stores and
 * back, and decide what counts as valid. Kept out of the page so the rules can be tested
 * without mounting a dialog.
 */

export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * How one round decides when it starts taking bets.
 *   inherit    — the lottery's own เวลาเปิด, which is what every round did before this
 *   time       — a wall clock ("รอบบ่ายเปิด 09:15"), stored as openBeforeSeconds
 *   afterPrev  — a span after the previous round's announcement ("เปิดตอนรอบเช้าออกผล"),
 *                stored as openAfterPreviousSeconds
 *
 * The last is the one a clock cannot express: it is anchored to an event, so it stays
 * correct when the previous round is announced late.
 */
export type RoundOpenMode = 'inherit' | 'time' | 'afterPrev'

/** One row of the editor. Strings are '' rather than undefined — inputs are controlled. */
export interface RoundDraft {
  time: string
  labelTh: string
  openMode: RoundOpenMode
  /** Wall clock for `time` mode. Held as a clock because that is what the admin picks. */
  openClock: string
  /** HH:MM read as a duration (not a clock) for `afterPrev` mode. */
  openAfterPrev: string
}

/** Ceiling on rounds per day, matching the draws_round_index_range CHECK. */
export const MAX_ROUNDS = 24

/**
 * Editor rows for a stored schedule. A lottery configured before multi-round support
 * reads back as a single row holding its old `drawTime`, so opening the dialog on any
 * of the lotteries running today shows exactly what it showed before.
 */
/** A row that inherits everything — the shape a brand-new round starts in. */
const BLANK_OPEN = { openMode: 'inherit' as const, openClock: '', openAfterPrev: '00:00' }

export function newRoundDraft(time = '', labelTh = ''): RoundDraft {
  return { time, labelTh, ...BLANK_OPEN }
}

export function roundDraftsFrom(cfg: DrawScheduleConfig | null | undefined): RoundDraft[] {
  const rounds = cfg ? normalizeRounds(cfg) : []
  if (rounds.length === 0) return [newRoundDraft(cfg?.drawTime ?? '20:30')]
  return rounds.map((r) => ({
    time: r.time,
    labelTh: r.labelTh ?? '',
    ...BLANK_OPEN,
    // afterPrev wins when both are stored, matching drawWindow's own precedence.
    ...(r.openAfterPreviousSeconds != null
      ? { openMode: 'afterPrev' as const, openAfterPrev: secondsToDuration(r.openAfterPreviousSeconds) }
      : r.openBeforeSeconds != null
        // Shown against this round's own announcement, which is the whole point of the
        // per-round value: the same duration reads as a different clock on every round.
        ? { openMode: 'time' as const, openClock: clockFromSecondsBefore(r.openBeforeSeconds, r.time) }
        : {}),
  }))
}

export type RoundsProblem =
  | { kind: 'time'; index: number }
  | { kind: 'duplicate'; index: number }
  | { kind: 'label'; index: number }
  | { kind: 'openTime'; index: number }
  | { kind: 'openAfterClose'; index: number }
  | { kind: 'openAfterPrevTooLate'; index: number }
  | null

/**
 * What is wrong with the drafts, or null when they are fine.
 *
 * Names are required only once there is more than one round: with a single round the
 * name is noise, with several it is the only thing that tells two งวด of the same day
 * apart on the member's result board. Mirrors the server's own check so the admin is
 * never allowed to submit something the API will refuse.
 *
 * `closeBeforeSeconds` is the lottery's betting cutoff, needed only to catch an opening
 * time that falls after it. That combination is not refused by the API — it stores
 * cleanly — but it produces a งวด that is 'scheduled' until it ages past its own close
 * time and then 'closed' without ever taking a bet, which is exactly the silent failure
 * the worker's inverted-window guard exists to log. Far better caught in the form.
 */
export function validateRounds(
  drafts: RoundDraft[], closeBeforeSeconds?: number,
): RoundsProblem {
  const seen = new Map<string, number>()
  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i]!
    if (!TIME_RE.test(d.time)) return { kind: 'time', index: i }
    if (seen.has(d.time)) return { kind: 'duplicate', index: i }
    seen.set(d.time, i)
    if (drafts.length > 1 && d.labelTh.trim() === '') return { kind: 'label', index: i }
    if (d.openMode === 'time') {
      if (!TIME_RE.test(d.openClock)) return { kind: 'openTime', index: i }
      if (
        closeBeforeSeconds != null &&
        secondsBeforeFromClock(d.openClock, d.time) <= closeBeforeSeconds
      ) {
        return { kind: 'openAfterClose', index: i }
      }
    }
    if (d.openMode === 'afterPrev' && !TIME_RE.test(d.openAfterPrev)) {
      return { kind: 'openTime', index: i }
    }
  }

  // "Opens after the previous announcement" is only checkable once every time parses,
  // because how much room it has is the gap to the round before it in the sorted order —
  // which is not the order the admin typed them in.
  if (closeBeforeSeconds != null) {
    const order = drafts
      .map((d, i) => ({ d, i }))
      .sort((a, b) => a.d.time.localeCompare(b.d.time))
    for (let k = 0; k < order.length; k++) {
      const { d, i } = order[k]!
      if (d.openMode !== 'afterPrev') continue
      // The first round of a day follows the last round of the day before.
      const previous = order[k === 0 ? order.length - 1 : k - 1]!.d
      if (durationToSeconds(d.openAfterPrev) + closeBeforeSeconds >= gapSeconds(previous.time, d.time)) {
        return { kind: 'openAfterPrevTooLate', index: i }
      }
    }
  }
  return null
}

/**
 * Seconds from one announcement to the next, wrapping over midnight. Two rounds at the
 * same time cannot happen (validateRounds refuses a duplicate) and a lone round follows
 * itself a day later, so a zero delta means a full day rather than no gap at all.
 */
function gapSeconds(previousTime: string, time: string): number {
  const mins = ((minutesOfDay(time) - minutesOfDay(previousTime)) % 1440 + 1440) % 1440
  return (mins === 0 ? 1440 : mins) * 60
}

/**
 * Drafts as the config to store, earliest first. The order is not cosmetic: a round's
 * position becomes the งวด's round_index, which is half the key the database uses to
 * refuse a duplicate.
 */
export function toRoundConfigs(drafts: RoundDraft[]): DrawRoundConfig[] {
  return drafts
    .map((d) => {
      const labelTh = d.labelTh.trim()
      return {
        time: d.time,
        ...(labelTh ? { labelTh } : {}),
        // Omitted entirely on 'inherit', so the round keeps following the lottery rather
        // than freezing today's inherited value into its own config.
        ...(d.openMode === 'time' && TIME_RE.test(d.openClock)
          // Measured against this round's own announcement.
          ? { openBeforeSeconds: secondsBeforeFromClock(d.openClock, d.time) }
          : d.openMode === 'afterPrev' && TIME_RE.test(d.openAfterPrev)
            ? { openAfterPreviousSeconds: durationToSeconds(d.openAfterPrev) }
            : {}),
      }
    })
    .sort((a, b) => a.time.localeCompare(b.time))
}

/** "รอบเช้า 10:30 · รอบบ่าย 14:00" — or just "20:30" for an unnamed single round. */
export function describeRounds(rounds: DrawRoundConfig[]): string {
  if (rounds.length === 0) return '--:--'
  return rounds.map((r) => (r.labelTh ? `${r.labelTh} ${r.time}` : r.time)).join(' · ')
}

/**
 * How far ahead of the announcement betting opens — the other half of the timetable,
 * stored as `bet_open_before_seconds` on the lottery itself rather than per round. Two
 * shapes cover what admins actually ask for: หวยไทย's ten-day run-up (a round number of
 * days, unrelated to any clock) and หวยฮานอย's "เปิดตี 1" (a fixed wall-clock time,
 * repeating every day the first round announces). The stored column doesn't know which
 * shape produced it, so the read direction below infers one from the value: a multiple of
 * a whole day reads back as days, anything else can only have come from a clock time.
 */

function minutesOfDay(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

function clockOf(totalMinutes: number): string {
  const m = ((totalMinutes % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

export type OpenBeforeMode = 'off' | 'days' | 'time'

/** Which editor mode reproduces a stored value, so opening the dialog shows it back. */
export function openBeforeModeOf(seconds: number | null): OpenBeforeMode {
  if (seconds == null) return 'off'
  return seconds % 86400 === 0 ? 'days' : 'time'
}

/** `bet_open_before_seconds` → the wall-clock time it opens at, given the first round's
 *  announcement time. The instant is the same either way; this only picks how to show it. */
export function clockFromSecondsBefore(seconds: number, firstRoundTime: string): string {
  return clockOf(minutesOfDay(firstRoundTime) - Math.round(seconds / 60))
}

/** The inverse — an admin-entered "opens at HH:MM every day" → seconds before the first
 *  round's announcement. Always resolves to the *same* day's opening, wrapping backward
 *  into the previous day only when the clock time sits after the announcement itself. */
export function secondsBeforeFromClock(openClock: string, firstRoundTime: string): number {
  const delta = ((minutesOfDay(firstRoundTime) - minutesOfDay(openClock)) % 1440 + 1440) % 1440
  return delta * 60
}

export function isValidClock(v: string): boolean {
  return TIME_RE.test(v)
}

/** "17 ชม. 30 นาที" / "9 ชม." / "20 นาที" — confirms what a clock-time choice means in
 *  the units an admin actually thinks in, next to the HH:MM they just typed. */
export function formatDurationTh(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m} นาที`
  if (m === 0) return `${h} ชม.`
  return `${h} ชม. ${m} นาที`
}

/**
 * The HH:MM widget doubles as a plain duration here — "how long after the previous
 * round's announcement", not a clock time — so unlike clockFromSecondsBefore there is
 * no anchor to measure against and no day wraparound to reason about.
 */
export function durationToSeconds(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return (h || 0) * 3600 + (m || 0) * 60
}

/** Seconds → "HH:MM", the inverse of durationToSeconds — for reading a stored value back. */
export function secondsToDuration(seconds: number): string {
  const totalMinutes = Math.max(0, Math.round(seconds / 60))
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
